import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
  Switch,
  FormControlLabel,
  Paper,
  Chip,
} from '@mui/material';
import {
  QrCodeScanner,
  Close,
  CameraAlt,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { Html5QrcodeScanner, Html5Qrcode } from 'html5-qrcode';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { closeDialog, addNotification, openDialog } from '../../store/slices/uiSlice';
import { FirestoreService } from '../../firebase/firestoreService';
import { QRCodeUtils } from '../../utils/qrCode';
import { InventoryItem } from '../../types/models';

const QRScannerDialog: React.FC = () => {
  const dispatch = useDispatch();
  const { dialog } = useSelector((state: RootState) => state.ui);
  
  const [isScanning, setIsScanning] = useState(false);
  const [scannedItem, setScannedItem] = useState<InventoryItem | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cameraEnabled, setCameraEnabled] = useState(true);
  
  const scannerRef = useRef<Html5QrcodeScanner | null>(null);
  const html5QrCodeRef = useRef<Html5Qrcode | null>(null);
  const scannerElementId = 'qr-scanner-container';

  const isOpen = dialog.open && dialog.type === 'qr-scanner';

  const cleanupScanner = () => {
    if (scannerRef.current) {
      try {
        scannerRef.current.clear();
      } catch (error) {
        console.warn('Error cleaning up scanner:', error);
      }
      scannerRef.current = null;
    }

    if (html5QrCodeRef.current) {
      try {
        html5QrCodeRef.current.stop();
      } catch (error) {
        console.warn('Error stopping camera:', error);
      }
      html5QrCodeRef.current = null;
    }

    setIsScanning(false);
  };

  const initializeScanner = useCallback(async () => {
    if (!isOpen) return;

    try {
      setError(null);
      setIsScanning(true);

      // Create scanner instance
      scannerRef.current = new Html5QrcodeScanner(
        scannerElementId,
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
          showTorchButtonIfSupported: true,
          showZoomSliderIfSupported: true,
          defaultZoomValueIfSupported: 2,
        },
        false // verbose
      );

      // Start scanning
      scannerRef.current.render(
        (decodedText) => onScanSuccess(decodedText),
        (errorMessage) => {
          // Only log actual errors, not "no QR code found" messages
          if (!errorMessage.includes('No QR code found')) {
            console.warn('QR scan error:', errorMessage);
          }
        }
      );
    } catch (error: any) {
      console.error('Error initializing scanner:', error);
      setError('Failed to initialize camera. Please check camera permissions.');
      setIsScanning(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && cameraEnabled) {
      initializeScanner();
    }

    return () => {
      cleanupScanner();
    };
  }, [isOpen, cameraEnabled, initializeScanner]);

  const onScanSuccess = async (decodedText: string) => {
    cleanupScanner();

    try {
      // Try to parse as our QR code format first
      const qrData = QRCodeUtils.parseQRCodeData(decodedText);
      
      if (qrData) {
        // This is our app's QR code format
        await handleAppQRCode(qrData.itemID);
      } else {
        // Check if it's a simple item ID
        const item = await FirestoreService.getInventoryItem(decodedText);
        if (item) {
          setScannedItem(item);
        } else {
          setError('QR code does not match any inventory item');
        }
      }
    } catch (error: any) {
      console.error('Error processing scanned QR code:', error);
      setError('Failed to process scanned QR code');
    }
  };

  const handleAppQRCode = async (itemID: string) => {
    try {
      // Find item by itemID (not document ID)
      const allItems = await FirestoreService.getAllInventoryItems();
      const item = allItems.find(i => i.itemID === itemID);
      
      if (item) {
        setScannedItem(item);
      } else {
        setError(`Item with ID "${itemID}" not found`);
      }
    } catch (error: any) {
      console.error('Error finding item:', error);
      setError('Failed to find scanned item');
    }
  };

  const handleClose = () => {
    cleanupScanner();
    dispatch(closeDialog());
    setScannedItem(null);
    setError(null);
    setCameraEnabled(true);
  };

  const handleCheckInOut = (item: InventoryItem) => {
    // Close scanner and open check in/out dialog
    handleClose();
    
    if (item.checkedOut) {
      dispatch(openDialog({ type: 'checkin', data: item }));
    } else {
      dispatch(openDialog({ type: 'checkout', data: item }));
    }
  };

  const handleViewItem = (item: InventoryItem) => {
    // Close scanner and show item details (you could create an item details dialog)
    handleClose();
    dispatch(addNotification({
      message: `Found item: ${item.itemName}`,
      type: 'info',
    }));
  };

  const restartScanning = () => {
    setScannedItem(null);
    setError(null);
    if (cameraEnabled) {
      initializeScanner();
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <QrCodeScanner color="primary" />
            <Typography variant="h6">QR Code Scanner</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Camera Toggle */}
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={cameraEnabled}
                onChange={(e) => {
                  setCameraEnabled(e.target.checked);
                  if (!e.target.checked) {
                    cleanupScanner();
                  }
                }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CameraAlt />
                <span>Enable Camera</span>
              </Box>
            }
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Scanned Item Result */}
        {scannedItem && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <CheckCircle />
              <Typography variant="h6">Item Found!</Typography>
            </Box>
            
            <Typography variant="subtitle1" gutterBottom>
              {scannedItem.itemName}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Chip
                label={scannedItem.checkedOut ? 'Checked Out' : 'Available'}
                color={scannedItem.checkedOut ? 'error' : 'success'}
                icon={scannedItem.checkedOut ? <Cancel /> : <CheckCircle />}
                size="small"
              />
              
              {scannedItem.itemGroup && (
                <Chip
                  label={scannedItem.itemGroup}
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={() => handleCheckInOut(scannedItem)}
                size="small"
              >
                {scannedItem.checkedOut ? 'Check In' : 'Check Out'}
              </Button>
              
              <Button
                variant="outlined"
                onClick={() => handleViewItem(scannedItem)}
                size="small"
              >
                View Details
              </Button>
              
              <Button
                variant="text"
                onClick={restartScanning}
                size="small"
              >
                Scan Another
              </Button>
            </Box>
          </Paper>
        )}

        {/* Scanner Container */}
        {cameraEnabled && !scannedItem && (
          <Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              Point your camera at a QR code to scan it
            </Typography>
            
            <Box
              id={scannerElementId}
              sx={{
                '& video': {
                  width: '100% !important',
                  height: 'auto !important',
                  borderRadius: 1,
                },
                '& #qr-shaded-region': {
                  borderRadius: 1,
                },
              }}
            />
            
            {isScanning && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, textAlign: 'center' }}>
                Scanning... Position QR code within the frame
              </Typography>
            )}
          </Box>
        )}

        {/* Manual Input Option */}
        {!cameraEnabled && (
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body1" paragraph>
              Camera is disabled. You can still check items in/out by searching in the inventory list.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setCameraEnabled(true)}
              startIcon={<CameraAlt />}
            >
              Enable Camera
            </Button>
          </Paper>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        {scannedItem && (
          <Button
            onClick={restartScanning}
            variant="outlined"
            startIcon={<QrCodeScanner />}
          >
            Scan Another
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default QRScannerDialog;